import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import classNames from "classnames";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../../components/Modal";
import Button from "../../../../components/Button";
import Checkbox from "../../../../components/Inputs/Checkbox";
import useApi from "../../../../utils/api";
import { useAccess } from "../../../../utils/hooks/access";
import { useCompanyDateFormat } from "../../../../utils/hooks/company";
import PendingAdjustmentModal, { formatSignedHours } from "../../../Profile/TimeOff/PendingAdjustment";

/**
 * Task list line "PTO balance adjustments to review": the pending adjustments of the manager's
 * team (settings `/time-off-plan/pending-adjustments`). The line opens the review list, a row opens
 * the same details window as Profile > Time off, with Approve / Reject. Rows can be ticked and
 * approved or rejected together (`/time-off-plan/adjustments/decide`).
 */
function PtoAdjustments() {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { authGet, authPost } = useApi();
    const { hasAccess } = useAccess("profile.canAddTeamPlan");
    const [rows, setRows] = useState([]);
    const [listOpen, setListOpen] = useState(false);
    const [reviewing, setReviewing] = useState(null);
    const [selected, setSelected] = useState([]);
    const [deciding, setDeciding] = useState(null);

    const fetch = useCallback(
        async (controller) => {
            try {
                const response = await authGet("/time-off-plan/pending-adjustments", { signal: controller?.signal });
                const list = Array.isArray(response) ? response : [];
                setRows(list);
                // keep only ticks of rows that are still waiting
                setSelected((ids) => ids.filter((id) => list.some((row) => row.id === id)));
            } catch (error) {
                // the task list stays usable without this line
                setRows([]);
            }
        },
        [authGet],
    );

    useEffect(() => {
        if (!hasAccess) return;
        const controller = new AbortController();
        fetch(controller);
        return () => controller.abort();
    }, [hasAccess, fetch]);

    const decideSelected = useCallback(
        async (decision) => {
            setDeciding(decision);
            try {
                const items = rows
                    .filter((row) => selected.includes(row.id))
                    .map((row) => ({ userId: row.employee?.id, userTimeOffPlan: row.userTimeOffPlan, adjustmentId: row.id }));
                const response = await authPost("/time-off-plan/adjustments/decide", { data: { decision, items } });
                if (response?.done) {
                    toast.success(t(decision === "approve" ? "pending-adjustments-approved-count" : "pending-adjustments-rejected-count", { count: response.done }));
                }
                if (response?.failed?.length) {
                    toast.error(t("pending-adjustments-failed-count", { count: response.failed.length }));
                }
            } catch (error) {
                toast.error(error?.message || t("something-went-wrong"));
            } finally {
                setDeciding(null);
                fetch();
            }
        },
        [rows, selected, authPost, fetch, t],
    );

    if (!hasAccess) return null;

    const count = rows.length;
    const allSelected = count > 0 && selected.length === count;
    const toggle = (id) => setSelected((ids) => (ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id]));
    const name = (row) => [row.employee?.firstName, row.employee?.lastName].filter(Boolean).join(" ") || row.employee?.employeeNumber;

    return (
        <>
            <div className="d-flex border-bottom align-items-center py-2">
                <div>
                    <i className="fa-solid fa-scale-balanced text-lg mr-3 text-warning" />
                </div>
                <div className="d-flex flex-column">
                    <span className="mb-1">{t("pto-adjustments-to-review")}</span>
                    <span
                        className={classNames("text-sm font-weight-bold text-warning", count > 0 && "cursor-pointer")}
                        onClick={count > 0 ? () => setListOpen(true) : undefined}
                    >
                        {t("review-pto-adjustments", { count })}
                        <i className="fa-solid fa-greater-than text-xs ml-2" />
                    </span>
                </div>
            </div>

            <Modal
                isOpen={listOpen && !reviewing}
                toggle={() => setListOpen(false)}
                size="lg"
                scrollable
                title={
                    <div className="d-flex align-items-center justify-content-between gap-2">
                        <span>{t("pto-adjustments-to-review")}</span>
                        <FontAwesomeIcon className="cursor-pointer" icon={faXmark} onClick={() => setListOpen(false)} />
                    </div>
                }
            >
                {count === 0 ? (
                    <p className="text-muted text-sm mb-0">{t("no-pto-adjustments-to-review")}</p>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: 32 }}>
                                        <Checkbox
                                            id="pto-adjustments-all"
                                            checked={allSelected}
                                            indeterminate={selected.length > 0 && !allSelected}
                                            onChange={() => setSelected(allSelected ? [] : rows.map((row) => row.id))}
                                        />
                                    </th>
                                    <th>{t("pending-adjustment-col-employee")}</th>
                                    <th>{t("pending-adjustment-col-plan")}</th>
                                    <th>{t("pending-adjustment-from")}</th>
                                    <th className="text-right">{t("pending-adjustment-col-hours")}</th>
                                    <th>{t("pending-adjustment-since")}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <Checkbox
                                                id={`pto-adjustment-${row.id}`}
                                                checked={selected.includes(row.id)}
                                                onChange={() => toggle(row.id)}
                                            />
                                        </td>
                                        <td style={{ whiteSpace: "normal" }}>
                                            <Link to={`/people/${row.employee?.id}/time-off`}>{name(row)}</Link>
                                            {row.employee?.employeeNumber && (
                                                <small className="d-block text-muted">{row.employee.employeeNumber}</small>
                                            )}
                                        </td>
                                        <td>{row.planCode}</td>
                                        <td style={{ whiteSpace: "normal" }}>
                                            {row.kind === "recalculation" || row.kind === "grant"
                                                ? t(`pending-adjustment-kind-${row.kind}`, { plan: (row.fromPlanCode || "").replace(/\s*\(.*\)$/, "") })
                                                : row.fromPlanCode}
                                        </td>
                                        <td className={classNames("text-right font-weight-bold", row.carry < 0 ? "text-danger" : "text-green")}>
                                            {formatSignedHours(row.carry)}
                                        </td>
                                        <td>{row.createdAt ? moment(row.createdAt).format(dateFormat) : ""}</td>
                                        <td className="text-right">
                                            <span
                                                className="text-primary font-weight-bold cursor-pointer"
                                                onClick={() => setReviewing(row)}
                                            >
                                                {t("pending-adjustment-review-action")}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {selected.length > 0 && (
                            <div className="d-flex align-items-center justify-content-end gap-2 border-top pt-2 mt-2 position-sticky bg-white" style={{ bottom: 0 }}>
                                <span className="text-sm text-muted mr-auto">{t("pending-adjustments-selected", { count: selected.length })}</span>
                                <Button
                                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                                    loading={deciding === "reject"}
                                    disabled={!!deciding}
                                    onClick={() => decideSelected("reject")}
                                >
                                    {t("pending-adjustments-reject-selected")}
                                </Button>
                                <Button
                                    color="primary"
                                    className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
                                    loading={deciding === "approve"}
                                    disabled={!!deciding}
                                    onClick={() => decideSelected("approve")}
                                >
                                    {t("pending-adjustments-approve-selected")}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <PendingAdjustmentModal
                userId={reviewing?.employee?.id}
                planId={reviewing?.userTimeOffPlan}
                adjustmentId={reviewing?.id}
                isOpen={!!reviewing}
                close={() => setReviewing(null)}
                onDecided={() => fetch()}
            />
        </>
    );
}

export default PtoAdjustments;
